import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { messages as api } from "../lib/api.js";
import { relativeTime } from "../lib/freshness.js";
import { countLabel } from "../lib/share-label.js";
import { onUnreadChanged } from "../lib/unread-events.js";
import { useLiveData } from "../lib/use-live-data.js";
import { useNow } from "../lib/use-now.js";

/**
 * New messages on any device you can see, until you read or acknowledge them.
 *
 * Absent when there is nothing new, rather than showing a zero: a badge that
 * is always present stops being looked at, and this one is only worth having
 * if its appearance means something.
 */

const REFRESH_MS = 30_000;

export function UnreadBadge() {
  const now = useNow();
  const { data, reload } = useLiveData(() => api.unread(), REFRESH_MS);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => onUnreadChanged(reload), [reload]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  if (!data || data.total === 0) return null;

  const readAll = async () => {
    await api.readAll();
    setOpen(false);
    reload();
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title="New messages on devices you can see"
        style={{
          background: "none",
          border: "1px solid var(--status-warning)",
          color: "var(--status-warning)",
          padding: "5px 10px",
          fontSize: 12,
          letterSpacing: "0.08em",
          cursor: "pointer",
          // Padded so the header does not shift as the count changes.
          whiteSpace: "pre",
        }}
      >
        [ {countLabel("NEW", data.total)} ]
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            zIndex: 30,
            width: 320,
            border: "1px solid var(--border-bright)",
            background: "var(--surface-raised)",
            padding: 10,
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.6)",
            fontSize: 12,
          }}
        >
          <div style={{ color: "var(--text-muted)", letterSpacing: "0.1em", marginBottom: 6 }}>
            NEW MESSAGES
          </div>
          {data.devices.map((d) => (
            <Link
              key={d.id}
              to={`/devices/${d.id}`}
              onClick={() => setOpen(false)}
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 8,
                padding: "6px 0",
                borderBottom: "1px solid var(--gridline)",
                color: "var(--text-primary)",
                textDecoration: "none",
              }}
            >
              <span>{d.name}</span>
              <span style={{ color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                {d.count} · {relativeTime(d.latest_at, now)}
              </span>
            </Link>
          ))}
          <button
            onClick={() => void readAll()}
            style={{
              marginTop: 10,
              background: "none",
              border: "1px solid var(--border)",
              color: "var(--text-secondary)",
              padding: "4px 8px",
              fontSize: 11,
              cursor: "pointer",
              letterSpacing: "0.08em",
            }}
          >
            [ MARK ALL READ ]
          </button>
        </div>
      )}
    </div>
  );
}
